import { Stack } from "@mui/material";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useUpdateIntakeQuestionMutation } from "src/api/services/intakeQuestionsApi";
import { useGetQuestionsQuery } from "src/api/services/questionsApi";
import {
  InputType,
  IntakeQuestion,
  Question,
  UpdateIntakeQuestion,
} from "src/api/types";
import Input from "src/components/FormInput";
import { MatchParam } from "src/constants/matches";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import DrawerForm from "src/shared/DrawerForm";

interface Props {
  question: IntakeQuestion;
  open: boolean;
  onClose: () => void;
}

const useInputs = () => {
  const [values, setValues] = useState<UpdateIntakeQuestion>({
    next_question: "",
    question_position: null,
  });

  const setFields = useCallback(
    <K extends keyof UpdateIntakeQuestion>(
      key: K,
      value: UpdateIntakeQuestion[K],
    ) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const { questions } = useGetQuestionsQuery(
    { from: 0, to: 1000 },
    {
      selectFromResult: ({ data }) => {
        return { questions: data?.data ?? [] };
      },
    },
  );

  const inputs: InputType<Question>[] = useMemo(
    () => [
      {
        type: "autocomplete",
        placeholder: "Next question",
        options: questions,
        value: questions.find((q) => q.slug === values.next_question) ?? null,
        onChange: (v) => setFields("next_question", v?.slug ?? ""),
        required: false,
        label: "Next Question",
        getOptionLabel: (q) => `${q.id}: ${q.title}`,
        getOptionDescription: (q) => q.subtitle ?? undefined,
      },
      {
        type: "number",
        label: "Position",
        name: "question_position",
        onChange: (v) => setFields("question_position", v),
        required: false,
        value: values.question_position ?? 0,
      },
    ],
    [questions, setFields, values.next_question, values.question_position],
  );

  return { inputs, values, setValues };
};

const UpdateQuestionDrawer = ({ onClose, open, question }: Props) => {
  const { inputs, setValues, values } = useInputs();
  const [loading, setLoading] = useState<boolean>(false);
  const errLog = useSentryLogger("error");
  const [updateQuestion] = useUpdateIntakeQuestionMutation();
  const { intakeId } = useParams<MatchParam<"INTAKE_ID">>();

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      try {
        await updateQuestion({
          intakeId: Number(intakeId),
          questionId: question.id,
          body: {
            next_question:
              values.next_question?.length === 0 ? null : values.next_question,
            question_position: values.question_position,
          },
        });

        setValues({
          next_question: null,
          question_position: null,
        });

        onClose();
      } catch (err) {
        errLog("Unable to update the intake question", err);
      } finally {
        setLoading(false);
      }
    },
    [
      errLog,
      intakeId,
      onClose,
      question.id,
      setValues,
      updateQuestion,
      values.next_question,
      values.question_position,
    ],
  );

  useEffect(() => {
    setValues({
      next_question: question.next_question?.slug,
      question_position: question.question_position,
    });
  }, [question.next_question, question.question_position, setValues]);

  return (
    <DrawerForm
      label="Update Intake Question"
      onClose={onClose}
      open={open}
      loading={loading}
      handleSubmit={handleSubmit}
    >
      <Stack gap={2} component={"form"}>
        <Stack gap={2}>
          {inputs.map((i) => (
            <Input key={i.label} current={i} />
          ))}
        </Stack>
      </Stack>
    </DrawerForm>
  );
};

export default UpdateQuestionDrawer;
